import { format } from "date-fns";
import { it } from 'date-fns/locale/it';
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from '../../../lib/redux/hooks';
import { SpinnerComponent } from "../../../lib/spinner";
import { ButtonComponent } from "../../../ui/molecules/button/newButton";
import { Calendar } from "../../../ui/molecules/calendar";
import { MadfarmInput } from "../../../ui/molecules/input/newInput";
import { MadfarmStaticSelect } from "../../../ui/molecules/select/newSelect/static";
import { ErrorPopup } from "../../../ui/organisms/errorPopup";
import { PopupForm } from "../../../ui/organisms/popupForm";
import { SectionDivider } from "../../../ui/organisms/section/divider";
import { FormSection } from "../../../ui/organisms/section/form";
import { SuccessPopup } from "../../../ui/organisms/successPopup";
import { Province, Region, regioniProvinceMap, regions } from "../../../utils";
import { provinceComuniMap } from "../../../utils/listaComuni";
import { findUserById } from "../../users/slice";
import { createCompanyCenter, resetCompanyCenterCreateError, resetCompanyCenterCreateErrors, resetCompanyCenterCreateRequest, setCompanyCenterCreateRequestField, setCompanyCenterCreateStatus, setCompanyCenterUpdateStatus, setDeleteCompanyCenterStatus, validateCompanyCenterCreateRequest } from "../feature/companyCenter/slice";
import { getCustomerById } from "../feature/slice";
import { ListCentriAziendaliEdit } from "./listCentriAziendaliEdit";

registerLocale('it', it)

type OptionType = { value: string; label: string };

export function BoxCentriAziendaliEdit() {
    const customerState = useAppSelector(state => state.customer)
    const companyCenterState = useAppSelector(state => state.companyCenter)
    const clientiState = useAppSelector(state => state.clienti)
    const usersState = useAppSelector(state => state.users)
    const dispatch = useAppDispatch()

    const [showPopup, setShowPopup] = useState(false)
    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([])
    const [selectedRegion, setSelectedRegion] = useState<OptionType | null>(null)
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([])
    const [selectedProvince, setSelectedProvince] = useState<OptionType | null>(null)

    const regionOptions: OptionType[] = Array.from(regioniProvinceMap.keys()).map(r => ({ value: r, label: r }))

    useEffect(() => {
        if (customerState.getCustomerByIdStatus === "successfully" && customerState.getCustomerByIdResponse !== undefined) {
            dispatch(setCompanyCenterCreateRequestField({
                field: 'customerId',
                value: customerState.getCustomerByIdResponse?.id
            }))
        }
    }, [customerState.getCustomerByIdStatus])

    useEffect(() => {
        if (clientiState.corporateCenterCreateResponseStatus === 'successfully') {
            dispatch(findUserById(usersState.selectedUserId))
        }
    }, [clientiState.corporateCenterCreateResponseStatus])

    useEffect(() => {
        if (selectedRegion) {
            const found = regions.find((r: Region) => r.name === selectedRegion.value)
            setProvinceOptions(found
                ? found.provinces.map((p: Province) => ({ value: p.code, label: p.name }))
                : []
            )
        } else {
            setProvinceOptions([])
        }
    }, [selectedRegion])

    useEffect(() => {
        if (selectedProvince) {
            const comuni = provinceComuniMap.get(selectedProvince.value) || []
            setComuneOptions(comuni.map((c: string) => ({ value: c, label: c })))
        } else {
            setComuneOptions([])
        }
    }, [selectedProvince])

    const handleRegionChange = (opt: OptionType | null) => {
        setSelectedRegion(opt)
        setSelectedProvince(null)
    }
    const handleProvinceChange = (opt: OptionType | null) => setSelectedProvince(opt)

    return (
        <>
            <div className="p-6 flex flex-col gap-6">
                {
                    companyCenterState.companyCenterCreateStatus === "loading" ?
                        <SpinnerComponent size={"small"} /> :
                        <>
                            {companyCenterState.companyCenterCreateStatus === "successfully" || companyCenterState.companyCenterUpdateStatus === "successfully" || companyCenterState.deleteCompanyCenterStatus === "successfully" ?
                                <div className="px-6 pt-3 pb-1 flex flex-col gap-2">
                                    <SuccessPopup
                                        active={companyCenterState.companyCenterCreateStatus === "successfully" || companyCenterState.companyCenterUpdateStatus === "successfully" || companyCenterState.deleteCompanyCenterStatus === "successfully"}
                                        message="Dati salvati correttamente."
                                        onClose={() => {
                                            dispatch(setCompanyCenterCreateStatus("idle"))
                                            dispatch(setCompanyCenterUpdateStatus("idle"))
                                            dispatch(setDeleteCompanyCenterStatus("idle"))
                                        }}
                                    />
                                </div>
                                : companyCenterState.companyCenterCreateStatus === "failed" || companyCenterState.companyCenterUpdateStatus === "failed" ?
                                    <div className="px-6 pt-3 pb-1 flex flex-col gap-2">
                                        <ErrorPopup
                                            active={companyCenterState.companyCenterCreateStatus === "failed" || companyCenterState.companyCenterUpdateStatus === "failed"}
                                            message="Si è verificato un errore durante la modifica del centro aziendale."
                                            onClose={() => {
                                                dispatch(setCompanyCenterCreateStatus("idle"))
                                                dispatch(setCompanyCenterUpdateStatus("idle"))
                                                dispatch(setDeleteCompanyCenterStatus("idle"))
                                            }}
                                        />
                                    </div> : null
                            }
                            <ListCentriAziendaliEdit />
                            <div>
                                <ButtonComponent
                                    size="sm"
                                    iconPosition="off"
                                    variant="outline"
                                    color="blue"
                                    label="Aggiungi Centro Aziendale"
                                    onClick={() => {
                                        dispatch(resetCompanyCenterCreateRequest())
                                        dispatch(resetCompanyCenterCreateErrors())
                                        if (customerState.getCustomerByIdResponse?.id) {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'customerId',
                                                value: customerState.getCustomerByIdResponse.id
                                            }))
                                        }
                                        setSelectedRegion(null)
                                        setSelectedProvince(null)
                                        setProvinceOptions([])
                                        setComuneOptions([])
                                        setShowPopup(true)
                                    }}
                                />
                            </div>
                        </>
                }
            </div>
            {
                showPopup && (
                    <PopupForm title="Nuovo centro aziendale" close={() => setShowPopup(false)}>
                        <div className="p-6 bg-white flex flex-col gap-6">
                            <FormSection>
                                <SectionDivider label="Identificazione" />
                                <div className="grid grid-cols-3 gap-4">
                                    <MadfarmInput
                                        required
                                        label="ID / Codice"
                                        placeholder="ID / Codice"
                                        supportingText={companyCenterState.companyCenterCreateErrors.code}
                                        error={!!companyCenterState.companyCenterCreateErrors.code}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.code}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'code',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('code'))
                                        }}
                                    />
                                    <div className="col-span-2">
                                        <MadfarmInput
                                            required
                                            label="Descrizione"
                                            placeholder="Descrizione"
                                            supportingText={companyCenterState.companyCenterCreateErrors.description}
                                            error={!!companyCenterState.companyCenterCreateErrors.description}
                                            defaultValue={companyCenterState.companyCenterCreateRequest.description}
                                            onChangeText={(text) => {
                                                dispatch(setCompanyCenterCreateRequestField({
                                                    field: 'description',
                                                    value: text
                                                }))
                                                dispatch(resetCompanyCenterCreateError('description'))
                                            }}
                                        />
                                    </div>
                                </div>
                            </FormSection>
                            <FormSection>
                                <SectionDivider label="Indirizzo" />
                                <MadfarmInput
                                    required
                                    label="Indirizzo"
                                    placeholder="Indirizzo"
                                    supportingText={companyCenterState.companyCenterCreateErrors.address}
                                    error={!!companyCenterState.companyCenterCreateErrors.address}
                                    defaultValue={companyCenterState.companyCenterCreateRequest.address}
                                    onChangeText={(text) => {
                                        dispatch(setCompanyCenterCreateRequestField({
                                            field: 'address',
                                            value: text
                                        }))
                                        dispatch(resetCompanyCenterCreateError('address'))
                                    }}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Regione"
                                        noOptionsMessage="Nessuna regione trovata"
                                        placeholder="Regione"
                                        error={!!companyCenterState.companyCenterCreateErrors.countryRegion}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.countryRegion}
                                        options={regionOptions}
                                        value={companyCenterState.companyCenterCreateRequest.countryRegion}
                                        onChange={(value) => {
                                            const option = value as OptionType | null
                                            const newValue = option ? option.value : null
                                            handleRegionChange(option)
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'countryRegion',
                                                value: newValue
                                            }))
                                            dispatch(resetCompanyCenterCreateError('countryRegion'))
                                        }}
                                    />
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Provincia"
                                        noOptionsMessage="Nessuna provincia trovata"
                                        placeholder="Provincia"
                                        error={!!companyCenterState.companyCenterCreateErrors.province}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.province}
                                        options={provinceOptions}
                                        value={companyCenterState.companyCenterCreateRequest.province}
                                        onChange={(value) => {
                                            const option = value as OptionType | null
                                            const newValue = option ? option.value : null
                                            handleProvinceChange(option)
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'province',
                                                value: newValue
                                            }))
                                            dispatch(resetCompanyCenterCreateError('province'))
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmStaticSelect
                                        required
                                        menuPortalTargetIsNotBody
                                        label="Comune"
                                        noOptionsMessage="Nessun comune trovato"
                                        placeholder="Comune"
                                        error={!!companyCenterState.companyCenterCreateErrors.city}
                                        errorLabel={companyCenterState.companyCenterCreateErrors.city}
                                        options={comuneOptions}
                                        value={companyCenterState.companyCenterCreateRequest.city}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'city',
                                                value: newValue
                                            }))
                                            dispatch(resetCompanyCenterCreateError('city'))
                                        }}
                                    />
                                    <MadfarmInput
                                        required
                                        label="CAP"
                                        placeholder="CAP"
                                        supportingText={companyCenterState.companyCenterCreateErrors.zipCode}
                                        error={!!companyCenterState.companyCenterCreateErrors.zipCode}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.zipCode}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'zipCode',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('zipCode'))
                                        }}
                                    />
                                </div>
                            </FormSection>
                            <FormSection>
                                <SectionDivider label="Geolocalizzazione" />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Latitudine"
                                        placeholder="Latitudine"
                                        supportingText={companyCenterState.companyCenterCreateErrors.latitude}
                                        error={!!companyCenterState.companyCenterCreateErrors.latitude}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.latitude ?? ""}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'latitude',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('latitude'))
                                        }}
                                    />
                                    <MadfarmInput
                                        label="Longitudine"
                                        placeholder="Longitudine"
                                        supportingText={companyCenterState.companyCenterCreateErrors.longitude}
                                        error={!!companyCenterState.companyCenterCreateErrors.longitude}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.longitude ?? ""}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'longitude',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('longitude'))
                                        }}
                                    />
                                </div>
                            </FormSection>
                            <FormSection>
                                <SectionDivider label="Contatti" />
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Referente"
                                        placeholder="Referente"
                                        supportingText={companyCenterState.companyCenterCreateErrors.referrer}
                                        error={!!companyCenterState.companyCenterCreateErrors.referrer}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.referrer ?? ""}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'referrer',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('referrer'))
                                        }}
                                    />
                                    <MadfarmInput
                                        label="Recapito"
                                        placeholder="Recapito"
                                        supportingText={companyCenterState.companyCenterCreateErrors.phone}
                                        error={!!companyCenterState.companyCenterCreateErrors.phone}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.phone}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'phone',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('phone'))
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <MadfarmInput
                                        label="Email"
                                        placeholder="Email"
                                        supportingText={companyCenterState.companyCenterCreateErrors.email}
                                        error={!!companyCenterState.companyCenterCreateErrors.email}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.email}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'email',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('email'))
                                        }}
                                    />
                                    <MadfarmInput
                                        label="PEC"
                                        placeholder="PEC"
                                        supportingText={companyCenterState.companyCenterCreateErrors.pec}
                                        error={!!companyCenterState.companyCenterCreateErrors.pec}
                                        defaultValue={companyCenterState.companyCenterCreateRequest.pec}
                                        onChangeText={(text) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'pec',
                                                value: text
                                            }))
                                            dispatch(resetCompanyCenterCreateError('pec'))
                                        }}
                                    />
                                </div>
                            </FormSection>
                            <FormSection>
                                <SectionDivider label="Date" />
                                <div className="grid grid-cols-2 gap-4">
                                    <Calendar
                                        required
                                        errorLabel={companyCenterState.companyCenterCreateErrors.activationDate}
                                        label="Data Ingresso"
                                        selected={companyCenterState.companyCenterCreateRequest.activationDate}
                                        onChange={(date) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'activationDate',
                                                value: format(new Date(date), 'yyyy-MM-dd')
                                            }))
                                            dispatch(resetCompanyCenterCreateError('activationDate'))
                                        }}
                                    />
                                    <Calendar
                                        errorLabel={companyCenterState.companyCenterCreateErrors.deactivationDate}
                                        label="Data Uscita"
                                        selected={companyCenterState.companyCenterCreateRequest.deactivationDate}
                                        onChange={(date) => {
                                            dispatch(setCompanyCenterCreateRequestField({
                                                field: 'deactivationDate',
                                                value: format(new Date(date), 'yyyy-MM-dd')
                                            }))
                                            dispatch(resetCompanyCenterCreateError('deactivationDate'))
                                        }}
                                    />
                                </div>
                            </FormSection>
                            <div className="flex justify-end pt-2 border-t border-neutral-200">
                                <ButtonComponent
                                    size="md"
                                    iconPosition="off"
                                    variant="solid"
                                    color="orange"
                                    label="Salva"
                                    onClick={() => {
                                        dispatch(validateCompanyCenterCreateRequest(companyCenterState.companyCenterCreateRequest))
                                            .unwrap()
                                            .then(() => {
                                                dispatch(createCompanyCenter(companyCenterState.companyCenterCreateRequest))
                                                    .unwrap()
                                                    .then(() => {
                                                        dispatch(getCustomerById(customerState.getCustomerByIdResponse?.id ?? ""))
                                                        setShowPopup(false)
                                                    })
                                                    .catch(() => { })
                                            })
                                            .catch(() => { })
                                    }}
                                />
                            </div>
                        </div>
                    </PopupForm>
                )
            }
        </>
    )
}